import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import { useDispatch, useSelector } from "react-redux";
import Iconify from "../../../components/iconify";
import Scrollbar from "../../../components/scrollbar";
import BranchTableRow from "../branch-table-row";
import BranchTableToolbar from "../branch-table-toolbar";
import {
  emptyRows,
  applyFilter,
  getComparator,
} from "../../../layouts/dashboard/common/utils";
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  TableRow,
  TableCell,
} from "@mui/material";
import { createBranch, branchData, hotelData } from "../../../features/dataAction"; // Import hotelData
import toast, { Toaster } from "react-hot-toast";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import useDebouncedFilter from "../../../hooks/use-debounce";
import EmptyRows from "../../../layouts/dashboard/common/Empty-rows/EmptyRows";
import TableHeadCustom from "../../../layouts/dashboard/common/Table-head/Table-Head";
import NoData from "../../../layouts/dashboard/common/No-data/No-data";
import CloseIcon from "@mui/icons-material/Close";

// ----------------------------------------------------------------------

export default function BranchViewPage() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState("");
  const [errors, setErrors] = useState({});
  const initialFormState = {
    name: "",
    address: "",
    phone: "",
    hotelBrand: "",
    branchAdminName: "",
    branchAdminEmail: "",
    branchAdminPassword: "",
  };
  const [branchdata, setBranchData] = useState(initialFormState);
  const [showPassword, setShowPassword] = useState(false);
  const [firstLoadDone, setFirstLoadDone] = useState(false);
  const handleClickShowPassword = () => setShowPassword((prev) => !prev);

  const validate = () => {
    const newErrors = {};

    Object.entries(branchdata).forEach(([key, value]) => {
      if (key === "hotelBrand" && role !== "super-admin") return;

      if (key === "phone") {
        if (!/^\d{10}$/.test(value)) {
          newErrors[key] = "Phone number must be exactly 10 digits";
        }
        return;
      }

      if (value === "" || value === 0) {
        newErrors[key] = `${key} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const dispatch = useDispatch();

  const { loading, branchDatas, hotelDatas, userLoginData } = useSelector(
    (state) => ({
      branchDatas: state.branchReducer.branchData,
      loading: state.branchReducer.loading,
      hotelDatas: state.hotelReducer.hotelDatas?.hotelBrands,
      userLoginData: state.userLoginReducer.userLoginData?.user,
    })
  );
  useEffect(() => {
    setRole(userLoginData?.role);
  }, [userLoginData]);

  useDebouncedFilter({
    filterValue: filterName,
    dispatch,
    fetchAction: branchData,
    page,
    rowsPerPage,
    setPage,
    setLoader: setFirstLoadDone,
  });

  // Fetch hotel data when modal opens for super-admin
  useEffect(() => {
    if (open && role === "super-admin" && (!hotelDatas || hotelDatas.length === 0)) {
      dispatch(hotelData());
    }
  }, [open, role, hotelDatas, dispatch]);

  const handleSubmit = () => {
    if (!validate()) return;

    const {
      name,
      address,
      phone,
      branchAdminName,
      branchAdminEmail,
      branchAdminPassword,
      hotelBrand,
    } = branchdata;

    dispatch(
      createBranch(
        name,
        address,
        phone,
        branchAdminName,
        branchAdminEmail,
        branchAdminPassword,
        role === "super-admin" ? hotelBrand : undefined,
        rowsPerPage,
        // role,
        (error) => error && toast.error(error)
      )
    );

    handleClose();
    setPage(0);
    setBranchData(initialFormState);
  };

  const handleChange = (field) => (e) => {
    setBranchData((prevState) => ({
      ...prevState,
      [field]: e.target.value,
    }));
    setErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
  };

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setBranchData(initialFormState);
  };

  const handleSort = (event, id) => {
    const isAsc = orderBy === id && order === "asc";
    if (id !== "") {
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(id);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };
  const dataFiltered = applyFilter({
    inputData: branchDatas?.branches || [],
    comparator: getComparator(order, orderBy),
    filterName,
  });

  const notFound = !dataFiltered.length && !!filterName;
  let fieldsInOrder = [
    { label: "Name*", name: "name", type: "text" },
    { label: "Address*", name: "address", type: "text" },
    { label: "Phone*", name: "phone", type: "text" },
    { label: "Hotel Brand*", name: "hotelBrand", options: hotelDatas },
    { label: "Branch Admin Name*", name: "branchAdminName", type: "text" },
    { label: "Branch Admin Email*", name: "branchAdminEmail", type: "email" },
    {
      label: "Branch Admin Password*",
      name: "branchAdminPassword",
      type: "password",
    },
  ];

  return (
    <Container>
      <Toaster position="top-right" />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={5}
      >
        <Box>
          <Typography variant="h4" color="primary">
            Branch List
          </Typography>
          <Typography style={{ fontSize: "13px", color: "gray" }}>
            Branch List
          </Typography>
        </Box>
        <Box style={{ display: "flex", gap: "10px" }}>
          <Button
            variant="contained"
            onClick={handleOpen}
            color="primary"
            startIcon={<Iconify icon="eva:plus-fill" />}
          >
            Create Branch
          </Button>
        </Box>
      </Stack>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={webStyle.style}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingBottom: "20px",
            }}
          >
            <Typography
              variant="h6"
              id="modal-title"
              gutterBottom
              style={webStyle.modalTitle}
            >
              Add New Branch
            </Typography>
            <IconButton onClick={handleClose} style={webStyle.closeButton}>
              <CloseIcon />
            </IconButton>
          </Box>
          {fieldsInOrder.map((field) => {
            if (field.name === "hotelBrand" && role !== "super-admin") {
              return null;
            }
            return field.options ? (
              <FormControl
                fullWidth
                variant="outlined"
                sx={{ mb: 2 }}
                error={!!errors[field.name]}
                key={field.name}
              >
                <InputLabel id={`${field.name}-label`}>
                  {field.label}
                </InputLabel>
                <Select
                  labelId={`${field.name}-label`}
                  value={branchdata[field.name]}
                  onChange={(e) => {
                    setBranchData((prevState) => ({
                      ...prevState,
                      [field.name]: e.target.value,
                    }));
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      [field.name]: "",
                    }));
                  }}
                  label={field.label}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        marginBottom: "20px",
                      },
                    },
                  }}
                >
                  {field.options?.map((opt) => (
                    <MenuItem
                      key={opt._id || opt.value || opt}
                      value={opt._id || opt.value || opt}
                    >
                      {opt.name || opt.label || opt}
                    </MenuItem>
                  ))}
                </Select>
                {errors[field.name] && (
                  <FormHelperText>{errors[field.name]}</FormHelperText>
                )}
              </FormControl>
            ) : (
              <TextField
                key={field.name}
                label={field.label}
                variant="outlined"
                type={
                  field.type === "password"
                    ? showPassword
                      ? "text"
                      : "password"
                    : field.type || "text"
                }
                value={branchdata[field.name] || ""}
                onChange={(e) => {
                  if (field.name === "phone" && e.target.value.length > 10)
                    return;
                  handleChange(field.name)(e);
                }}
                error={!!errors[field.name]}
                helperText={errors[field.name]}
                fullWidth
                margin="normal"
                sx={{ mb: 2 }}
                inputProps={
                  field.name === "phone"
                    ? { maxLength: 10, inputMode: "numeric" }
                    : {}
                }
                InputProps={
                  field.type === "password"
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleClickShowPassword}
                              edge="end"
                            >
                              {showPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
              />
            );
          })}
          <Box
            style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}
          >
            <Button
              variant="contained"
              style={webStyle.cancelBtn}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              style={webStyle.saveBtn}
              onClick={handleSubmit}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Modal>
      <Card>
        <BranchTableToolbar
          filterName={filterName}
          onFilterName={handleFilterByName}
        />
        <Scrollbar>
          <TableContainer sx={{ overflow: "unset" }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHeadCustom
                order={order}
                orderBy={orderBy}
                rowCount={branchDatas?.branches?.length}
                onRequestSort={handleSort}
                headLabel={[
                  { id: "Id", label: "Id", width: "30%" },
                  { id: "Name", label: "Name", width: "10%" },
                  { id: "Address", label: "Address", width: "20%" },
                  { id: "Phone", label: "Phone", width: "15%" },
                  { id: "Admin User", label: "Admin User", width: "15%" },
                  {
                    id: "Action",
                    label: "Action",
                    width: "10%",
                    align: "right",
                  },
                ]}
              />

              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ padding: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        // height: rowsPerPage * 77,
                        height: "200px",
                        width: "100%",
                      }}
                    >
                      <CircularProgress size={30} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : dataFiltered.length > 0 ? (
                <>
                  {dataFiltered.map((row) => (
                    <BranchTableRow
                      key={row.id}
                      id={row._id}
                      name={row.name}
                      address={row.address}
                      phone={row.phone}
                      page={page}
                      perPage={rowsPerPage}
                      adminUser={row.adminUser?.name}
                    />
                  ))}
                  <EmptyRows
                    height={77}
                    emptyRows={emptyRows(
                      page,
                      rowsPerPage,
                      branchDatas?.totalBranches
                    )}
                  />
                </>
              ) : (
                <NoData query={filterName} />
              )}
            </Table>
          </TableContainer>
        </Scrollbar>

        <TablePagination
          page={page}
          component="div"
          count={branchDatas?.totalBranches}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
      <Toaster position="top-right" />
    </Container>
  );
}
const webStyle = {
  style: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxWidth: 650,
    width: "90%",
    bgcolor: "background.paper",
    borderRadius: 1,
    boxShadow: 24,
    // p: 4,
    p: { xs: "20px", sm: "34px" },
  },
  styleModal2: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 500,
    bgcolor: "background.paper",
    borderRadius: 1,
    boxShadow: 24,
    p: 4,
  },
  modalTitle: {
    marginBottom: "0px",
    color: "#00A651",
  },
  textField: {
    width: "100%",
    borderColor: "gray",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "gray",
      },
      "&:hover fieldset": {
        borderColor: "darkgray",
      },
      "&.Mui-focused fieldset": {
        borderColor: "gray",
      },
    },
  },
  textArea: {
    width: "100%",
    height: "150px",
  },
  formControl: {
    marginBottom: "16px",
    width: "100%",
  },
  radioLabel: {
    color: "red !important",
  },
  cancelBtn: {
    backgroundColor: "white",
    border: "1px solid green",
    color: "green",
  },
  saveBtn: {
    backgroundColor: "green",
    border: "1px solid green",
    color: "white",
  },
};
